import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Minus, Plus } from "lucide-react";
import { CHF } from "@/lib/apiClient";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

export const ProductDialog = ({ product, open, onClose }) => {
  const { addToCart } = useStore();
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({}); // groupId -> [optionId]
  const [comment, setComment] = useState("");

  React.useEffect(() => {
    if (open && product) {
      setQty(1);
      setComment("");
      const init = {};
      (product.addon_groups || []).forEach((g) => {
        if (g.required && g.type === "radio" && g.options.length) init[g.id] = [g.options[0].id];
      });
      setSelected(init);
    }
  }, [open, product]);

  const groups = product?.addon_groups || [];

  const toggle = (group, opt) => {
    setSelected((prev) => {
      const cur = prev[group.id] || [];
      if (group.type === "radio") return { ...prev, [group.id]: [opt.id] };
      if (cur.includes(opt.id)) return { ...prev, [group.id]: cur.filter((x) => x !== opt.id) };
      if (group.max && cur.length >= group.max) return prev;
      return { ...prev, [group.id]: [...cur, opt.id] };
    });
  };

  const selectedAddons = useMemo(() => {
    const out = [];
    groups.forEach((g) => {
      (selected[g.id] || []).forEach((oid) => {
        const opt = g.options.find((o) => o.id === oid);
        if (opt) out.push({ group: g.name, name: opt.name, price: opt.price });
      });
    });
    return out;
  }, [selected, groups]);

  const unit = (product?.price || 0) + selectedAddons.reduce((s, a) => s + a.price, 0);

  const add = () => {
    for (const g of groups) {
      if (g.required && (selected[g.id] || []).length < Math.max(1, g.min)) {
        toast.error(`Veuillez choisir : ${g.name}`);
        return;
      }
    }
    addToCart({
      product_id: product.id, name: product.name, qty, base_price: product.price,
      selected_addons: selectedAddons, comment: comment.trim(),
    });
    toast.success(`${product.name} ajouté au panier`);
    onClose();
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-[calc(100vw-1rem)] max-w-lg max-h-[calc(100dvh-1rem)] flex flex-col rounded-none border-2 border-black p-0 gap-0"
        data-testid="product-dialog"
      >
        {product.image && (
          <div className="h-44 w-full overflow-hidden border-b border-black shrink-0">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>
        )}
        <DialogHeader className="p-5 pb-2 shrink-0">
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tighter">{product.name}</DialogTitle>
          {product.description && <p className="text-sm text-muted-foreground">{product.description}</p>}
        </DialogHeader>

        <div className="px-5 space-y-5 overflow-y-auto flex-1">
          {groups.map((g) => (
            <div key={g.id} data-testid={`addon-group-${g.id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-sm">{g.name} {g.required && <span className="text-brand-red">*</span>}</p>
                <span className="text-xs text-muted-foreground">{g.type === "radio" ? "Choix unique" : `Max ${g.max || "∞"}`}</span>
              </div>
              <div className="space-y-2">
                {g.options.map((o) => {
                  const checked = (selected[g.id] || []).includes(o.id);
                  return (
                    <div key={o.id} data-testid={`addon-opt-${o.id}`} onClick={() => toggle(g, o)} className={`flex items-center justify-between p-3 border cursor-pointer transition-colors ${checked ? "border-brand-red bg-brand-red/5" : "border-black/20"}`}>
                      <span className="flex items-center gap-3">
                        {g.type === "radio" ? (
                          <span className={`w-4 h-4 rounded-full border-2 ${checked ? "border-brand-red bg-brand-red" : "border-black/40"}`} />
                        ) : (
                          <Checkbox checked={checked} onCheckedChange={() => toggle(g, o)} onClick={(e) => e.stopPropagation()} />
                        )}
                        <span className="text-sm">{o.name}</span>
                      </span>
                      <span className="text-sm font-medium">{o.price > 0 ? `+ ${CHF(o.price)}` : "Inclus"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <p className="font-semibold text-sm mb-2">Commentaire</p>
            <Textarea
              value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Ex : sans coriandre, sauce à part…"
              className="rounded-none border-black/30 resize-none" data-testid="product-comment"
            />
          </div>
        </div>

        <div className="p-5 border-t border-black flex items-center gap-4 shrink-0">
          <div className="flex items-center border-2 border-black">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-11 flex items-center justify-center" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
            <span className="w-10 text-center font-bold" data-testid="qty-value">{qty}</span>
            <button onClick={() => setQty((q) => q + 1)} className="w-10 h-11 flex items-center justify-center" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
          </div>
          <Button onClick={add} data-testid="add-to-cart-btn" className="flex-1 rounded-none bg-brand-red hover:bg-brand-red/90 h-11">
            Ajouter · {CHF(unit * qty)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
